import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Employee, Department } from '../types/models';
import { EmployeeService } from '../services/employeeService';
import { EmployeeForm } from '../components/forms/EmployeeForm';
import { DepartmentService } from '../services/departmentService';
import { useCompany } from '../contexts/CompanyContext';
import { API_BASE_URL } from '../services/api';

export const EmployeesPage: React.FC = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null); // null = все сотрудники
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { selectedCompany } = useCompany();

  useEffect(() => {
    loadEmployees();
  }, [selectedDepartmentId]);

  // Загружаем список отделов при выборе контрагента
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const response = await DepartmentService.getAll();
        if (response.success) {
          setDepartments(response.data);
        }
      } catch (_) {
        // игнорируем, ошибки покажем при загрузке сотрудников при необходимости
      }
    };

    // сбрасываем выбор подразделения при смене контрагента
    setSelectedDepartmentId(null);
    loadDepartments();
  }, [selectedCompany]);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      if (selectedDepartmentId) {
        // Загружаем сотрудников по отделу
        response = await EmployeeService.getByDepartment(selectedDepartmentId);
      } else {
        // Загружаем всех сотрудников
        response = await EmployeeService.getAll();
      }

      if (response.success) {
        setEmployees(response.data);
      } else {
        setError(response.message || 'Ошибка загрузки сотрудников');
      }
    } catch (err) {
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Инструктаж считается действующим, если он проводился и с даты последнего
   * проведения прошло не более 6 месяцев.
   */
  const isInstructionValid = (date?: string | null): boolean => {
    if (!date) {
      return false;
    }

    const lastDate = new Date(date);
    if (isNaN(lastDate.getTime())) {
      return false;
    }

    const deadline = new Date();
    deadline.setMonth(deadline.getMonth() - 6);

    return lastDate >= deadline;
  };

  const renderInstructionStatus = (date?: string | null) => {
    const valid = isInstructionValid(date);
    const title = date
      ? `Последний инструктаж: ${new Date(date).toLocaleDateString('ru-RU')}`
      : 'Инструктаж не проводился';

    return (
      <span
        title={title}
        style={{ color: valid ? '#28a745' : '#dc3545', fontSize: '18px', fontWeight: 'bold' }}
      >
        {valid ? '✓' : '✗'}
      </span>
    );
  };

  const handleCreate = () => {
    setEditingEmployee(null);
    setShowForm(true);
  };

  const handleEdit = (employee: Employee) => {
    setEditingEmployee(employee);
    setShowForm(true);
  };

  const handleDelete = async (employee: Employee) => {
    const fullName = `${employee.surname} ${employee.name} ${employee.patronymic}`.trim();
    if (window.confirm(`Вы уверены, что хотите удалить сотрудника "${fullName}"?`)) {
      try {
        const response = await EmployeeService.delete(employee.id);
        if (response.success) {
          loadEmployees(); // Перезагрузить список
        } else {
          setError(response.message || 'Ошибка удаления');
        }
      } catch (err) {
        setError('Ошибка удаления сотрудника');
      }
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingEmployee(null);
    loadEmployees(); // Перезагрузить список
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingEmployee(null);
  };

  const getFullName = (employee: Employee) => {
    return `${employee.surname} ${employee.name} ${employee.patronymic}`.trim();
  };

  const handleViewDocuments = (employee: Employee) => {
    navigate(`/employees/${employee.id}/documents`);
  };

  const handleDownloadAll = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/Employee/DownloadAllFile?companyId=${selectedCompany?.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'Все_сотрудники.docx';

        if (contentDisposition) {
          const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/);
          if (filenameStarMatch && filenameStarMatch[1]) {
            filename = decodeURIComponent(filenameStarMatch[1]);
          } else {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, '');
              filename = decodeURIComponent(filename);
            }
          }
        }

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Ошибка при скачивании файла:', response.statusText);
        alert('Ошибка при скачивании файла');
      }
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error);
      alert('Ошибка при скачивании файла');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Проверяем расширение файла
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      alert('Пожалуйста, выберите файл Excel (.xlsx или .xls)');
      return;
    }

    if (!selectedCompany) {
      alert('Пожалуйста, выберите компанию');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Создаем FormData для отправки файла
      const formData = new FormData();
      formData.append('file', file);

      // Отправляем файл на бэкенд
      const response = await fetch(`${API_BASE_URL}/Employee/UploadFile?companyId=${selectedCompany.id}`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        
        // Очищаем input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        // Перезагружаем список сотрудников
        await loadEmployees();

        // Показываем результат
        if (result.success !== false) {
          const successCount = result.successCount || result.data?.successCount || 0;
          const errorCount = result.errorCount || result.data?.errorCount || 0;
          const message = result.message || result.data?.message;
          
          if (successCount > 0) {
            alert(`Успешно загружено сотрудников: ${successCount}${errorCount > 0 ? `\nОшибок: ${errorCount}` : ''}${message ? `\n${message}` : ''}`);
          } else if (message) {
            alert(message);
          } else {
            alert('Файл обработан');
          }
        } else {
          const errorMessage = result.message || result.data?.message || 'Ошибка при загрузке файла';
          alert(errorMessage);
          setError(errorMessage);
        }
      } else {
        const errorText = await response.text();
        let errorMessage = 'Ошибка при загрузке файла';
        
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        
        alert(errorMessage);
        setError(errorMessage);
      }
    } catch (err: any) {
      console.error('Ошибка при загрузке файла:', err);
      const errorMessage = err.message || 'Ошибка при загрузке файла';
      alert(errorMessage);
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Сотрудники</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select
            className="form-input"
            style={{ width: '200px' }}
            value={selectedDepartmentId || ''}
            onChange={(e) => setSelectedDepartmentId(Number(e.target.value) || null)}
          >
            <option value="">Все структурные подразделения</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={handleCreate}>
            Добавить сотрудника
          </button>
          <button className="btn btn-secondary" onClick={handleDownloadAll}>
            Выгрузить все
          </button>
          <button className="btn btn-secondary" onClick={handleUploadClick} disabled={uploading}>
            {uploading ? 'Загрузка...' : 'Загрузить'}
          </button>
          <a
            className="btn btn-secondary"
            href={`${process.env.PUBLIC_URL}/import_template.xlsx`}
            download="import_template.xlsx"
          >
            Скачать шаблон для загрузки
          </a>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>ФИО</th>
              <th>Отдел</th>
              <th>Должности</th>
              <th>Противопожарный инструктаж</th>
              <th>По охране труда</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((employee) => (
              <tr key={employee.id}>
                <td>{employee.id}</td>
                <td>{getFullName(employee)}</td>
                <td>{employee.department?.name || `Отдел ${employee.departmentId}`}</td>
                <td>
                  {employee.jobs?.map(job => job.name).join(', ') || 'Не назначены'}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {renderInstructionStatus(employee.lastFirePreventionInstructionDate)}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {renderInstructionStatus(employee.lastLaborSafetyInstructionDate)}
                </td>
                <td>
                  <button
                    className="btn btn-primary"
                    style={{ marginRight: '5px' }}
                    onClick={() => handleViewDocuments(employee)}
                  >
                    Документы
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ marginRight: '5px' }}
                    onClick={() => handleEdit(employee)}
                  >
                    Редактировать
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDelete(employee)}
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <EmployeeForm
          employee={editingEmployee || undefined}
          departmentId={selectedDepartmentId || undefined}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}
    </div>
  );
};
